
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import UserDropdownMenu from "./UserDropdownMenu";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { fetchUserServiceProviders } from "@/services/providers/providerOwnerService";
import { isUnauthorizedPlatformApiError } from "@/services/platformApi/client";
import { useSubscription } from "@/hooks/useSubscription";
import { useEffect } from "react";
import { hasPendingPayment } from "@/hooks/subscription/checkoutHelpers";
import { Plus } from "lucide-react";
import { debugLogger } from '@/utils/debug';
import { useTranslation } from "react-i18next";

const DesktopNav = () => {
  const { user, signOut, isAdmin, loading } = useAuth();
  const { t } = useTranslation('common');
  const { 
    data: subscriptionStatus, 
    refetch: refetchSubscription, 
    isLoading: isLoadingSubscription
  } = useSubscription();
  
  // Refetch subscription data on login and when returning from any external redirect
  useEffect(() => {
    if (user) {
      // Add a small delay to ensure auth is fully initialized
      const timer = setTimeout(() => {
        refetchSubscription();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, refetchSubscription]);

  // Also check for URL subscription=success parameter to force a refetch
  useEffect(() => {
    const checkForRedirect = () => {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('subscription') === 'success') {
        debugLogger.log("Detected subscription=success in URL, refreshing subscription status");
        refetchSubscription();
      }
    };
    
    // Check on component mount
    checkForRedirect();
    
    // Also add a listener for URL changes
    window.addEventListener('popstate', checkForRedirect);
    
    return () => {
      window.removeEventListener('popstate', checkForRedirect);
    };
  }, [refetchSubscription]);
  
  // Query to get the user's listings so the header CTA can link to the editable listing when appropriate.
  const {
    data: userListings = [],
    isLoading: isLoadingUserListings,
  } = useQuery({
    queryKey: ['userListingsForHeaderCta', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      try {
        return await fetchUserServiceProviders(user.id);
      } catch (error) {
        if (!isUnauthorizedPlatformApiError(error)) {
          console.error("Error fetching user listings for header CTA:", error);
        }
        return [];
      }
    },
    enabled: !loading && !!user,
  });
  
  // Query to check if user has pending payment
  const { data: isPendingPayment = false } = useQuery({
    queryKey: ['pendingPayment', user?.id],
    queryFn: async () => {
      if (!user) return false;
      return await hasPendingPayment();
    },
    enabled: !loading && !!user,
    refetchInterval: 30000,
  });
  
  // Check if user is on free plan or WhatsApp tier and has any listing
  const hasAnyListing = userListings.length > 0;
  const isFreeTier = !subscriptionStatus?.subscribed || subscriptionStatus?.tier === 'free_tier';
  const isWhatsAppTier = subscriptionStatus?.tier === 'whatsapp_tier';
  const hasPendingVerification = subscriptionStatus?.status === 'requires_action';
  const editableListingTarget = userListings.find((listing) => listing.status === 'pending')
    ?? userListings.find((listing) => listing.status === 'approved')
    ?? userListings.find((listing) => listing.status === 'rejected');
  const shouldShowUpdateListing = isWhatsAppTier && Boolean(editableListingTarget?.id);
  
  // Determine if user needs upgrade instead of submitting another listing
  const needsUpgrade = (isFreeTier || isWhatsAppTier) && hasAnyListing;

  // Determine button text and destination based on subscription status
  const getSubmitButtonProps = () => {
    // If subscription status is still loading, show loading state
    if (user && (isLoadingSubscription || isLoadingUserListings)) {
      return {
        text: t("buttons.loading"),
        destination: "#",
        disabled: true,
        icon: null
      };
    }
    
    // If user has a pending payment verification
    if (hasPendingVerification || isPendingPayment) {
      return {
        text: t("userMenu.paymentPending"),
        destination: "#",
        disabled: true,
        icon: null
      };
    }

    if (shouldShowUpdateListing && editableListingTarget?.id) {
      return {
        text: t("userMenu.updateListing"),
        destination: `/submit-listing?edit=${editableListingTarget.id}`,
        disabled: false,
        icon: null
      };
    }
    
    // If user needs to upgrade instead of submitting another listing
    if (needsUpgrade) {
      return {
        text: t("userMenu.upgradeToPremium"),
        destination: "/profile?tab=subscription",
        disabled: false,
        icon: null
      };
    }
    
    // Default submit listing button
    return {
      text: t("userMenu.addListing"),
      destination: "/submit-listing",
      disabled: false,
      icon: Plus
    };
  };
  
  const buttonProps = getSubmitButtonProps();

  const navItems = [
    { label: t("navigation.home"), path: "/" },
    { label: t("navigation.explore"), path: "/explore" },
    { label: t("navigation.about"), path: "/about" },
    { label: t("navigation.pricing"), path: "/pricing" }
  ];

  return (
    <div className="hidden min-w-0 flex-1 items-center justify-between gap-6 md:flex">
      <nav className="flex min-w-0 items-center gap-6">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="text-sm font-medium text-brand-slate transition-colors hover:text-brand-ink"
          >
            {item.label}
          </Link>
        ))}
      </nav>

      {user ? (
        <div className="flex shrink-0 items-center gap-4">
          <Link to={buttonProps.destination}>
            <Button size="sm" variant="outline" disabled={buttonProps.disabled} className="flex items-center gap-2 border-brand-cloud text-brand-ink hover:bg-brand-mist hover:text-brand-ink">
              {buttonProps.icon && <buttonProps.icon className="h-4 w-4" />}
              {buttonProps.text}
            </Button>
          </Link>
          {isAdmin && (
            <Link to="/admin">
              <Button size="sm" variant="secondary" className="flex items-center gap-2">
                {t("navigation.admin")}
              </Button>
            </Link>
          )}
          <UserDropdownMenu user={user} signOut={signOut} isAdmin={isAdmin} />
        </div>
      ) : (
        <div className="flex shrink-0 items-center space-x-4">
          <Link to="/auth?tab=login">
            <Button size="sm" variant="outline" className="border-brand-cloud text-brand-ink hover:bg-brand-mist hover:text-brand-ink">{t("navigation.login")}</Button>
          </Link>
          <Link to="/auth?tab=signup">
            <Button size="sm" className="bg-brand-ink hover:bg-brand-charcoal text-white">{t("navigation.signup")}</Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default DesktopNav;
